import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { DonutChart } from '../design/widgets/Chart'
import { api } from '../lib/api'
import styles from './Dashboard.module.css'

interface LeadStats {
  total: number
  by_status: Record<string, number>
  by_source: Record<string, number>
  scanned: number
  scan_coverage_pct: number
  avg_score: number
}
interface DealStats {
  total: number
  by_stage: Record<string, number>
  won: number
  lost: number
  pipeline_mrr_cents: number
  close_rate_pct: number
}
interface PipeRow { stage: string; count: number; total_mrr: number }
interface Activity { id: string; entity_type: string; event_type: string; title: string; created_at: string }
interface DigestTask { id: string; title: string; due_date: string | null; priority: string; project_id: string }
interface DigestProject { id: string; name: string; due_date: string | null; client_company_name: string | null }
interface DigestTicket { id: string; subject: string; number: string | null; resolution_due_at: string | null }
interface DigestReminder { id: string; message: string; remind_at: string }
interface WorkDigest {
  counts: { overdue_tasks: number; due_soon_tasks: number; overdue_projects: number; sla_risk_tickets: number; due_reminders: number }
  overdue_tasks: DigestTask[]
  due_soon_tasks: DigestTask[]
  overdue_projects: DigestProject[]
  sla_risk_tickets: DigestTicket[]
  due_reminders: DigestReminder[]
}

const STATUS_COLORS: Record<string, string> = {
  new: '#38bdf8', contacted: '#fbbf24', qualified: '#34d399',
  disqualified: '#56788e', converted: '#a78bfa',
}
const STAGE_LABEL: Record<string, string> = {
  lead: 'Lead', qualified: 'Qualified', proposal_draft: 'Proposal Draft',
  proposal_presented: 'Proposal Sent', negotiation: 'Negotiation', won: 'Won', lost: 'Lost',
}
const OPEN_STAGES = ['lead', 'qualified', 'proposal_draft', 'proposal_presented', 'negotiation']

function fmtRel(iso: string): string {
  const s = (Date.now() - new Date(iso).getTime()) / 1000
  if (s < 60) return 'just now'
  if (s < 3600) return `${Math.floor(s / 60)}m ago`
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`
  return `${Math.floor(s / 86400)}d ago`
}

function chip(color: string): React.CSSProperties {
  return {
    fontSize: 11, fontWeight: 600, padding: '2px 9px', borderRadius: 999,
    color, background: `color-mix(in srgb, ${color} 14%, transparent)`,
    border: `1px solid color-mix(in srgb, ${color} 35%, transparent)`,
  }
}

function fmtDue(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  const days = Math.round((d.getTime() - Date.now()) / 86400000)
  if (days < -1) return `${Math.abs(days)}d overdue`
  if (days === -1 || days === 0) return days === 0 ? 'due today' : '1d overdue'
  if (days === 1) return 'due tomorrow'
  return `in ${days}d`
}

export default function Dashboard() {
  const { claims } = useAuth()
  const navigate = useNavigate()
  const name = claims?.email?.split('@')[0] ?? 'there'
  const [lead, setLead] = useState<LeadStats | null>(null)
  const [deal, setDeal] = useState<DealStats | null>(null)
  const [pipe, setPipe] = useState<PipeRow[]>([])
  const [activity, setActivity] = useState<Activity[]>([])
  const [work, setWork] = useState<WorkDigest | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    Promise.allSettled([
      api.get<LeadStats>('/api/lead-engine/leads/meta/stats'),
      api.get<DealStats>('/api/deal-desk/deals/meta/stats'),
      api.get<PipeRow[]>('/api/deal-desk/deals/pipeline'),
      api.get<{ items: Activity[] }>('/api/timeline/timeline?page_size=8'),
      api.get<WorkDigest>('/api/project-mgmt/reminders?scope=mine&days=7'),
    ]).then(([l, d, p, a, w]) => {
      if (l.status === 'fulfilled') setLead(l.value)
      if (d.status === 'fulfilled') setDeal(d.value)
      if (p.status === 'fulfilled') setPipe(Array.isArray(p.value) ? p.value : [])
      if (a.status === 'fulfilled') setActivity(a.value?.items || [])
      if (w.status === 'fulfilled') setWork(w.value)
      setLoading(false)
    })
  }, [])

  const openDeals = deal ? deal.total - deal.won - deal.lost : 0
  const pipelineMrr = pipe.filter(r => OPEN_STAGES.includes(r.stage)).reduce((s, r) => s + (r.total_mrr || 0), 0)
  const maxPipe = Math.max(1, ...pipe.map(r => r.count))
  const statusSegments = lead
    ? Object.entries(lead.by_status).map(([k, v]) => ({ label: k, value: v, color: STATUS_COLORS[k] || '#56788e' }))
    : []
  const topSources = lead ? Object.entries(lead.by_source).sort((a, b) => b[1] - a[1]).slice(0, 6) : []
  const maxSource = Math.max(1, ...topSources.map(([, v]) => v))

  const KPIS = [
    { label: 'Total Leads', value: lead ? lead.total.toLocaleString() : '—', sub: lead ? `avg score ${lead.avg_score}` : '', color: 'var(--ac)' },
    { label: 'Open Deals', value: deal ? String(openDeals) : '—', sub: deal ? `${deal.total} total deals` : '', color: 'var(--ye)' },
    { label: 'Pipeline MRR', value: pipelineMrr > 0 ? `$${Math.round(pipelineMrr).toLocaleString()}` : '$0', sub: 'open stages', color: 'var(--gr)' },
    { label: 'Win Rate', value: deal ? `${deal.close_rate_pct}%` : '—', sub: deal ? `${deal.won} won · ${deal.lost} lost` : '', color: 'var(--ac)' },
  ]

  if (loading) return <div className={styles.page}><p className={styles.sub} style={{ padding: 24 }}>Loading dashboard…</p></div>

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Dashboard</h1>
          <p className={styles.sub}>Welcome back, {name} · Northwest Arkansas + Eastern Oklahoma</p>
        </div>
      </div>

      {/* KPI row — real metrics */}
      <div className={styles.kr}>
        {KPIS.map(k => (
          <div key={k.label} className={`${styles.gc} ${styles.kpi}`}>
            <div className={styles.kpiHead}><span className={styles.kpiLbl}>{k.label}</span></div>
            <div className={styles.kpiVal} style={{ color: k.color }}>{k.value}</div>
            <div className={styles.kpiFoot}><span className={styles.kpiSub}>{k.sub}</span></div>
          </div>
        ))}
      </div>

      {/* My Work — real, from project_mgmt reminders digest */}
      {work && (() => {
        const c = work.counts
        const total = c.overdue_tasks + c.due_soon_tasks + c.overdue_projects + c.sla_risk_tickets + c.due_reminders
        type Row = { id: string; label: string; meta: string; tag: string; color: string; to: string }
        const rows: Row[] = []
        for (const t of work.overdue_tasks.slice(0, 4))
          rows.push({ id: 't-' + t.id, label: t.title, meta: fmtDue(t.due_date), tag: 'Task', color: 'var(--re)', to: '/app/projects' })
        for (const p of work.overdue_projects.slice(0, 3))
          rows.push({ id: 'p-' + p.id, label: p.name, meta: fmtDue(p.due_date), tag: 'Project', color: 'var(--re)', to: '/app/projects' })
        for (const k of work.sla_risk_tickets.slice(0, 3))
          rows.push({ id: 'k-' + k.id, label: k.subject, meta: k.resolution_due_at ? fmtDue(k.resolution_due_at) : 'SLA risk', tag: 'Ticket', color: 'var(--ye)', to: '/app/tickets' })
        for (const t of work.due_soon_tasks.slice(0, 3))
          rows.push({ id: 'ds-' + t.id, label: t.title, meta: fmtDue(t.due_date), tag: 'Task', color: 'var(--ye)', to: '/app/projects' })
        for (const r of work.due_reminders.slice(0, 3))
          rows.push({ id: 'r-' + r.id, label: r.message, meta: fmtDue(r.remind_at), tag: 'Reminder', color: 'var(--ac)', to: '/app/projects' })
        return (
          <div className={`${styles.gc} ${styles.card}`}>
            <div className={styles.cardTop}>
              <span className={styles.cardTitle}>My Work</span>
              <span className={styles.kpiSub}>
                {total === 0 ? 'all caught up' : `${total} item${total === 1 ? '' : 's'} need attention`}
              </span>
            </div>
            {total === 0 ? (
              <div className={styles.kpiSub} style={{ padding: '6px 2px' }}>You're all caught up. 🎉</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 6 }}>
                  {c.overdue_tasks > 0 && <span style={chip('var(--re)')}>{c.overdue_tasks} overdue task{c.overdue_tasks === 1 ? '' : 's'}</span>}
                  {c.overdue_projects > 0 && <span style={chip('var(--re)')}>{c.overdue_projects} overdue project{c.overdue_projects === 1 ? '' : 's'}</span>}
                  {c.sla_risk_tickets > 0 && <span style={chip('var(--ye)')}>{c.sla_risk_tickets} SLA-risk ticket{c.sla_risk_tickets === 1 ? '' : 's'}</span>}
                  {c.due_soon_tasks > 0 && <span style={chip('var(--ye)')}>{c.due_soon_tasks} due soon</span>}
                  {c.due_reminders > 0 && <span style={chip('var(--ac)')}>{c.due_reminders} reminder{c.due_reminders === 1 ? '' : 's'}</span>}
                </div>
                {rows.map(r => (
                  <button key={r.id} onClick={() => navigate(r.to)}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
                             background: 'none', border: 'none', borderLeft: `3px solid ${r.color}`,
                             padding: '6px 10px', cursor: 'pointer', borderRadius: 4, color: 'var(--tx)' }}>
                    <span style={{ flex: 1, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.label}</span>
                    <span style={{ fontSize: 11, color: r.color, flexShrink: 0 }}>{r.meta}</span>
                    <span className={styles.kpiSub} style={{ fontSize: 10, flexShrink: 0, width: 52, textAlign: 'right' }}>{r.tag}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )
      })()}

      {/* Deal pipeline by stage — real */}
      <div className={`${styles.gc} ${styles.card}`}>
        <div className={styles.cardTop}>
          <span className={styles.cardTitle}>Deal Pipeline by Stage</span>
          <span className={styles.kpiSub}>{deal?.total ?? 0} deals</span>
        </div>
        <div className={styles.pipeBars}>
          {pipe.filter(r => STAGE_LABEL[r.stage]).length === 0 && <div className={styles.kpiSub}>No deals yet — create one from a lead.</div>}
          {pipe.filter(r => STAGE_LABEL[r.stage]).map(r => (
            <div key={r.stage} className={styles.pipeBarRow}>
              <span className={styles.pipeBarLbl}>{STAGE_LABEL[r.stage]}</span>
              <div className={styles.pipeBarTrack}>
                <div className={styles.pipeBarFill} style={{ width: `${(r.count / maxPipe) * 100}%`, background: OPEN_STAGES.includes(r.stage) ? 'var(--ac)' : r.stage === 'won' ? 'var(--gr)' : 'var(--re)' }} />
              </div>
              <span className={styles.pipeBarVal}>{r.count}{r.total_mrr > 0 ? ` · $${Math.round(r.total_mrr).toLocaleString()}/mo` : ''}</span>
            </div>
          ))}
        </div>
      </div>

      <div className={styles.row2}>
        {/* Leads by status — real donut */}
        <div className={`${styles.gc} ${styles.card}`}>
          <div className={styles.cardTop}><span className={styles.cardTitle}>Leads by Status</span></div>
          <div className={styles.donutRow}>
            <DonutChart
              segments={statusSegments.length ? statusSegments : [{ label: 'none', value: 1, color: '#56788e' }]}
              size={120} thickness={20} centerValue={(lead?.total ?? 0).toLocaleString()} centerLabel="leads"
            />
            <div className={styles.pipeLegend}>
              {statusSegments.map(s => (
                <div key={s.label} className={styles.legRow}>
                  <span className={styles.legDot} style={{ background: s.color }} />
                  <span className={styles.legLbl} style={{ textTransform: 'capitalize' }}>{s.label}</span>
                  <span className={styles.legVal}>{s.value.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Recent activity — real */}
        <div className={`${styles.gc} ${styles.tc}`}>
          <div className={styles.cardBodyTitle}>Recent Activity</div>
          <div className={styles.thead} style={{ gridTemplateColumns: '1fr auto auto' }}>
            <span>Event</span><span>Type</span><span style={{ textAlign: 'right' }}>When</span>
          </div>
          {activity.length === 0 && (
            <div className={styles.trow} style={{ gridTemplateColumns: '1fr' }}>
              <span className={styles.tEvent} style={{ color: 'var(--tx2)' }}>No recent activity yet.</span>
            </div>
          )}
          {activity.map(a => (
            <div key={a.id} className={styles.trow} style={{ gridTemplateColumns: '1fr auto auto' }}>
              <span className={styles.tEvent}>{a.title}</span>
              <span><span className={`${styles.badge} ${styles.bAc}`}><span className={styles.bdot} />{a.entity_type}</span></span>
              <span className={styles.tWhen}>{fmtRel(a.created_at)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Top lead sources — real */}
      {topSources.length > 0 && (
        <div className={`${styles.gc} ${styles.card}`}>
          <div className={styles.cardTop}><span className={styles.cardTitle}>Top Lead Sources</span></div>
          <div className={styles.pipeBars}>
            {topSources.map(([src, n]) => (
              <div key={src} className={styles.pipeBarRow}>
                <span className={styles.pipeBarLbl}>{src}</span>
                <div className={styles.pipeBarTrack}><div className={styles.pipeBarFill} style={{ width: `${(n / maxSource) * 100}%`, background: 'var(--ye)' }} /></div>
                <span className={styles.pipeBarVal}>{n.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
